import React, { useEffect, useState } from 'react';
import { Star, ChevronUp, ChevronDown, Car } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_HIDDEN = 'newOrder.myVehiclesHidden';

/**
 * Horizontal "Quick start from my vehicles" strip rendered above the
 * Vehicle Identification form on Step 1 of New Order. Each card is a
 * past vehicle the user has already ordered for — clicking [Fill]
 * autofills brand/model/engine/ECU on the form below. Form remains
 * always visible — the strip is purely additive.
 *
 * Props:
 *   token             string  — auth bearer
 *   onFill(vehicle)   func    — called with {vehicle_type, manufacturer,
 *                               model, engine, ecu_type} when a card
 *                               is clicked
 */
export default function MyVehiclesStrip({ token, onFill }) {
  const { t } = useTranslation('new-order');
  const [vehicles, setVehicles] = useState(null); // null = loading
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(STORAGE_HIDDEN) === '1'; } catch (e) { return false; }
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/orders/my-vehicles?limit=6`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) { if (!cancelled) setVehicles([]); return; }
        const j = await r.json();
        if (!cancelled) setVehicles(Array.isArray(j.vehicles) ? j.vehicles : []);
      } catch (e) {
        if (!cancelled) setVehicles([]);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Hide the whole strip entirely when the user has no past orders —
  // showing an empty "Quick start" frame would be confusing on a fresh
  // account. Returns null so the form below sits flush against the
  // stepper, no wasted vertical space.
  if (vehicles === null) return null;            // loading: render nothing
  if (vehicles.length === 0) return null;        // no history: render nothing

  const toggleHidden = () => {
    const next = !hidden;
    setHidden(next);
    try { localStorage.setItem(STORAGE_HIDDEN, next ? '1' : '0'); } catch (e) { /* ignore */ }
  };

  // Strip a few legacy artifacts that show up in older orders (<br>,
  // duplicate spaces) so the cards look clean.
  const clean = (s) => String(s || '').replace(/<br\s*\/?>/gi, ' ').replace(/\s{2,}/g, ' ').trim();

  return (
    <div
      className="mb-4 bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl overflow-hidden"
      data-testid="my-vehicles-strip"
    >
      {/* Header */}
      <button
        type="button"
        onClick={toggleHidden}
        data-testid="my-vehicles-toggle"
        className="w-full flex items-center justify-between px-4 py-2.5 bg-white/60 hover:bg-white/80 transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-cyan-900">
          <Car className="w-4 h-4 text-cyan-700" />
          {t('quick_start.title', { defaultValue: 'Quick start from my vehicles' })}
          <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-cyan-100 text-cyan-700">
            {vehicles.length}
          </span>
        </span>
        <span className="flex items-center gap-1 text-xs font-medium text-cyan-700">
          {hidden
            ? t('quick_start.show', { defaultValue: 'Show' })
            : t('quick_start.hide', { defaultValue: 'Hide' })}
          {hidden ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
        </span>
      </button>

      {/* Cards row */}
      {!hidden && (
        <div className="px-3 py-3">
          <div className="flex gap-2.5 overflow-x-auto pb-1" data-testid="my-vehicles-cards">
            {vehicles.map((v, idx) => (
              <VehicleCard key={idx} v={v} idx={idx} clean={clean} onFill={onFill} t={t} />
            ))}
          </div>
          <p className="text-[11px] text-cyan-800/70 mt-2 ml-1">
            {t('quick_start.or_fill_manually', { defaultValue: 'Or fill in manually below ↓' })}
          </p>
        </div>
      )}
    </div>
  );
}

function VehicleCard({ v, idx, clean, onFill, t }) {
  const brand  = clean(v.manufacturer) || '—';
  const model  = clean(v.model);
  const engine = clean(v.engine);
  const ecu    = clean(v.ecu_type);
  const isTop  = !!v.is_top;
  // "Most ordered" / "Last ordered" / "N orders" — pick the right one.
  let badge;
  if (isTop && v.order_count > 1) {
    badge = (<span className="flex items-center gap-0.5 text-amber-700"><Star className="w-3 h-3 fill-amber-400 stroke-amber-500" />{t('quick_start.badge_most', { defaultValue: 'Most' })}</span>);
  } else if (v.order_count === 1) {
    badge = <span className="text-slate-500">{t('quick_start.badge_last_ordered', { defaultValue: 'Last ordered' })}</span>;
  } else {
    badge = <span className="text-slate-600">{t('quick_start.badge_n_orders', { count: v.order_count, defaultValue: `${v.order_count} orders` })}</span>;
  }

  return (
    <button
      type="button"
      onClick={() => onFill?.(v)}
      data-testid={`my-vehicle-card-${idx}`}
      className="group flex-shrink-0 w-44 text-left bg-white rounded-lg border-2 border-slate-200 hover:border-cyan-500 hover:shadow-md transition-all p-3 flex flex-col"
    >
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">
        {brand}
      </div>
      <div className="text-sm font-semibold text-slate-800 truncate" title={model || ''}>
        {model || '—'}
      </div>
      <div className="text-xs text-slate-600 truncate mt-0.5" title={engine || ''}>
        {engine || ''}
      </div>
      {ecu && (
        <div className="text-[11px] text-slate-500 truncate mt-0.5" title={ecu}>
          {ecu}
        </div>
      )}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
        <span className="text-[10px] font-medium">{badge}</span>
        <span
          className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-cyan-100 text-cyan-700 group-hover:bg-cyan-500 group-hover:text-white transition-colors"
          data-testid={`my-vehicle-fill-${idx}`}
        >
          {t('quick_start.fill', { defaultValue: 'Fill' })}
        </span>
      </div>
    </button>
  );
}
